import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { getJSON, postJSON, patchJSON, FetchResponse } from 'utils/fetch'
import { VendorScreeningSubmitData, VendorSiteVisitSchedulerInnerStatus, Calendar, VendorScreeningStep } from './types'

dayjs.extend(timezone)

async function schedulerMethodJSON(
  method: typeof patchJSON | typeof postJSON,
  path: string,
  body: Record<string, unknown>,
  calendarId: string,
  onCalendarChange: React.Dispatch<React.SetStateAction<Calendar>>,
  attempt
): Promise<FetchResponse | false> {
  // If we're on our third attempt, get out with a failure
  if (attempt > 3) {
    return false
  }

  const response = await method(path, body)

  let retry = false
  if (response.code == 422) {
    // Their session changed in some way. Grab new token and try again.
    try {
      // Refresh our calendar info.
      const cResponse = await getJSON<Calendar>(`/vendor_site_visits/${calendarId}`)
      onCalendarChange((prev) => ({ ...prev, ...cResponse }))
      // CSRF needs to be updated immediately to have an impact on the
      // next attempt
      document.querySelector('meta[name=csrf-param]')?.setAttribute('content', cResponse.csrf_param)
      document.querySelector('meta[name=csrf-token]')?.setAttribute('content', cResponse.csrf_token)

      retry = true
    } catch {
      // We'll return the 422 errored response in this case.
    }

    if (retry) {
      const newResponse = await schedulerMethodJSON(method, path, body, calendarId, onCalendarChange, attempt + 1)
      // If our last attempt had a non-false response, use that.
      // Otherwise use the response from this scope's attempt even if it is bad.
      if (newResponse) return newResponse
    }
  }

  return response
}

export async function schedulerPostJSON(
  path: string,
  body: Record<string, unknown>,
  calendarId: string,
  onCalendarChange: React.Dispatch<React.SetStateAction<Calendar>>,
  attempt = 1
): Promise<FetchResponse> {
  const response = await schedulerMethodJSON(postJSON, path, body, calendarId, onCalendarChange, attempt)
  return response as FetchResponse
}

export async function schedulerPatchJSON(
  path: string,
  body: Record<string, unknown>,
  calendarId: string,
  onCalendarChange: React.Dispatch<React.SetStateAction<Calendar>>,
  attempt = 1
): Promise<FetchResponse> {
  const response = await schedulerMethodJSON(patchJSON, path, body, calendarId, onCalendarChange, attempt)
  return response as FetchResponse
}

export function computeVendorScreeningSteps(calendar: Calendar): VendorScreeningStep[] {
  const result: VendorScreeningStep[] = []

  result.push({
    kind: 'assign',
    q: 'Please review the project scope and confirm your continued interest',
    options: [
      { label: 'Yes, I am interested and will assign a project manager', ok: true },
      { label: 'No, I am not interested', ok: false },
    ],
    negativeOutcome: 'decline',
  })

  if (calendar.property.property_plan_files.length > 0) {
    result.push({
      kind: 'docs',
      q: 'Please confirm you have reviewed the additional project documents (e.g., plans, renderings)',
      options: [
        { label: 'Yes, I have reviewed the plans', ok: true },
        { label: 'No, but I will review before the site visit', ok: true },
        { label: 'No, I am not planning to review', ok: false },
      ],
      negativeOutcome: 'disqualify',
    })
  }

  if (calendar.property.potential_project_challenges) {
    result.push({
      kind: 'potential_project_challenges',
      q: 'Are you comfortable handling the following potential project challenges?',
      options: [
        { label: 'Yes, I can handle these challenges', ok: true },
        { label: 'No, I cannot or am not willing to handle these challenges', ok: false },
      ],
      negativeOutcome: 'disqualify',
    })
  }

  if (calendar.property.additional_notes) {
    result.push({
      kind: 'additional_notes',
      q: 'Please see additional notes from the advisor.',
      options: [],
      negativeOutcome: 'disqualify',
    })
  }

  const startStr = calendar.availabilities.map((e) => e.time.start).sort()[0] // default string sort works well here
  const endStr = calendar.availabilities.map((e) => e.time.end).sort()[calendar.availabilities.length - 1] // default string sort works well here
  const siteVisitsWindowStart = dayjs(startStr).tz(calendar.property.time_zone)
  const siteVisitsWindowEnd = dayjs(endStr).tz(calendar.property.time_zone)

  result.push({
    kind: 'deadlines',
    q: `Site visits are being held from ${siteVisitsWindowStart.format('MMM D')} to ${siteVisitsWindowEnd.format('MMM D')}, with bids due ${calendar.property.days_for_bid_turnaround || 7} day${calendar.property.days_for_bid_turnaround == 1 ? '' : 's'} after your site visit. Can you meet this deadline?`,
    options: [
      { label: 'Yes, I can meet this deadline', ok: true },
      { label: 'No, I cannot meet this deadline', ok: false },
    ],
    negativeOutcome: 'disqualify',
  })

  return result
}

export const patchVendorSiteVisit = (
  public_id: string,
  body: VendorScreeningSubmitData,
  signal: AbortSignal
): Promise<FetchResponse<{ calendar: Calendar }>> => {
  return patchJSON<{ calendar: Calendar }>(`/vendor_site_visits/${public_id}`, body, signal)
}

export const useUpdateVendorSiteVisit = (
  public_id: string,
  numberOfSteps: number,
  setCalendar: Dispatch<SetStateAction<Calendar>>,
  setInnerStatus: Dispatch<SetStateAction<VendorSiteVisitSchedulerInnerStatus>>,
  setVendorScreeningStepIndex: Dispatch<SetStateAction<number>>
) => {
  const [dataToSubmit, setDataToSubmit] = useState<null | VendorScreeningSubmitData>(null)
  const [error, setError] = useState<null | string>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [setError])

  useEffect(() => {
    if (dataToSubmit == null) return

    const controller = new AbortController()
    const _ = async () => {
      try {
        const response = await patchVendorSiteVisit(public_id, dataToSubmit, controller.signal)
        if (response.isError) {
          console.error(response.jsonBody)
          setError('An error occurred, please reload the page.')
        } else {
          const { calendar } = response.jsonBody
          setCalendar(calendar)
          const reassign = calendar.assigned_vendor_contact.id != calendar.current_vendor_contact.id
          const decline = dataToSubmit.vendor_screening_responses.some((e) => e.outcome == 'decline')
          const disqualify = dataToSubmit.vendor_screening_responses.some((e) => e.outcome == 'disqualify')
          const final = dataToSubmit.vendor_screening_responses.length == numberOfSteps
          setVendorScreeningStepIndex((oldVendorScreeningStepIndex) => oldVendorScreeningStepIndex + 1)
          if (reassign) {
            setInnerStatus('reassigned')
          } else if (decline) {
            setInnerStatus('declined')
          } else if (disqualify) {
            setInnerStatus('disqualified')
          } else if (final) {
            setInnerStatus(null)
          }
        }
        setDataToSubmit(null)
      } catch (error) {
        if (!controller.signal.aborted) {
          setDataToSubmit(null)
          console.error(error)
          setError('An error occurred, please reload the page.')
        }
      }
    }
    _()

    return () => {
      controller.abort()
    }
  }, [dataToSubmit, numberOfSteps, public_id, setCalendar, setInnerStatus, setVendorScreeningStepIndex])

  return { submitting: dataToSubmit != null, setDataToSubmit, error, clearError }
}
